import { useRef } from "react";
import { Button, Checkbox } from "@legal-500/legal500-design-system";
import { rolesData } from "@legal-500/permissions";
import type { ColumnMapping } from "./parseCsv.js";
import { ACCOUNT_OWNER, type CsvUpload } from "./useCsvUpload.js";
import { OwnerWarning } from "./OwnerWarning.js";
import styles from "./RoleProvisioning.module.css";

/**
 * Step 1 of the Role Provisioning tab: get a CSV in (file or paste), show how
 * it was read, let the operator correct the column mapping and pick the roles,
 * and surface the rows the scan will never see. Presentational — all state is
 * the tab's `useCsvUpload`.
 */

/**
 * The roles this tool can grant: every account-services registry role, read
 * from the shared registry so a new role appears here without a UI change.
 * `account_owner` is in the list but exclusive — see `useCsvUpload.toggleRole`.
 */
export const GRANTABLE_ROLES = rolesData
  .filter(role => role.context === "account-services")
  .sort((roleA, roleB) => roleA.name.localeCompare(roleB.name));

/** How many client-side problems to list inline before collapsing to a count. */
const PROBLEMS_SHOWN = 20;

type UploadStepProps = {
  upload: CsvUpload;
  disabled: boolean;
  onScan: () => void;
};

function ColumnSelect({
  id,
  label,
  headers,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  headers: string[];
  value: number | null;
  disabled: boolean;
  onChange: (index: number | null) => void;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={styles.select}
        value={value === null ? "" : String(value)}
        disabled={disabled}
        onChange={event => onChange(event.target.value === "" ? null : Number(event.target.value))}
      >
        <option value="">— not mapped —</option>
        {headers.map((header, index) => (
          // Headers can repeat in a hand-edited file; the index is the identity.
          // eslint-disable-next-line react/no-array-index-key
          <option key={index} value={String(index)}>
            {header === "" ? `(column ${index + 1})` : header}
          </option>
        ))}
      </select>
    </div>
  );
}

export function UploadStep({ upload, disabled, onScan }: UploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { parsed, mapping, roles, built, ownerMode } = upload;

  const setMapping = (patch: Partial<ColumnMapping>) => upload.setMapping({ ...mapping, ...patch });

  /**
   * Owner is never disabled: ticking it clears and greys out every other role
   * (the hook replaces the selection), unticking it hands them back.
   */
  const isRoleDisabled = (name: string) => disabled || (ownerMode && name !== ACCOUNT_OWNER);

  const rowCount = built.rows.length;
  const problemCount = built.problems.length;

  return (
    <div className={styles.stack}>
      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Upload the CSV</div>
        <div className={styles.stack}>
          <div className={styles.rowNote}>
            One row per person. Required column: <code>email</code> (any header containing
            &quot;email&quot;). Optional: <code>firm_ref</code> — when filled it restricts the row
            to that firm instead of the user&apos;s only membership. Other columns are ignored.
            Excel exports (cp1252, <code>;</code>-separated, title lines above the header) are fine.
          </div>
          <textarea
            className={styles.pasteBox}
            aria-label="Role provisioning CSV"
            placeholder={"email,firm_ref\nsomeone@firm.com,\n…"}
            value={upload.text}
            disabled={disabled}
            onChange={event => upload.setText(event.target.value)}
          />
          <input
            ref={fileInputRef}
            type="file"
            aria-label="Role provisioning CSV file"
            accept=".csv,.txt,text/csv"
            className={styles.hiddenInput}
            disabled={disabled}
            onChange={event => {
              const chosen = event.target.files?.[0];
              // Clear the input's value, otherwise picking the SAME file again
              // fires no change event and appears to do nothing.
              event.target.value = "";
              if (chosen && !disabled) {
                void upload.loadFile(chosen);
              }
            }}
          />
          <div className={styles.toolbar}>
            <Button
              variant="outlined"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse
            </Button>
            {upload.fileName ? (
              <span className={styles.rowNote}>
                {upload.fileName}
                {upload.encoding ? ` · decoded as ${upload.encoding}` : ""}
              </span>
            ) : null}
            <span className={styles.spacer} />
            <Button
              variant="outlined"
              disabled={disabled || upload.text.trim() === ""}
              onClick={() => upload.parseText(upload.text)}
            >
              Parse CSV
            </Button>
          </div>
        </div>
      </div>

      {upload.readError ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {upload.readError}
        </div>
      ) : null}

      {parsed && parsed.errors.length > 0 ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          <ul className={styles.errorList}>
            {parsed.errors.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {parsed && parsed.errors.length === 0 ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>2 · Columns and roles</div>
          <div className={styles.stack}>
            <div className={styles.rowNote}>
              Header on line {parsed.headerLineNumber}, delimiter{" "}
              <code>{parsed.delimiter === "\t" ? "tab" : parsed.delimiter}</code>,{" "}
              {parsed.records.length.toLocaleString()} data row
              {parsed.records.length === 1 ? "" : "s"}.
            </div>
            <div className={styles.fields}>
              <ColumnSelect
                id="role-provisioning-email-column"
                label="Email column (required)"
                headers={parsed.headers}
                value={mapping.email}
                disabled={disabled}
                onChange={email => setMapping({ email })}
              />
              <ColumnSelect
                id="role-provisioning-firm-ref-column"
                label="firm_ref column (optional)"
                headers={parsed.headers}
                value={mapping.firmRef}
                disabled={disabled}
                onChange={firmRef => setMapping({ firmRef })}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Roles to grant</span>
              <ul className={styles.roleList}>
                {GRANTABLE_ROLES.map(role => (
                  <li key={role.name}>
                    <Checkbox
                      id={`role-provisioning-role-${role.name}`}
                      label={role.name}
                      aria-label={`Role ${role.name}`}
                      title={role.description}
                      checked={roles.includes(role.name)}
                      disabled={isRoleDisabled(role.name)}
                      onChange={event => upload.toggleRole(role.name, event.target.checked)}
                    />
                  </li>
                ))}
              </ul>
            </div>

            {ownerMode ? <OwnerWarning /> : null}

            <div className={styles.toolbar}>
              <span className={styles.resultCount} aria-live="polite">
                {rowCount.toLocaleString()} valid row{rowCount === 1 ? "" : "s"}
                {problemCount > 0 ? ` · ${problemCount.toLocaleString()} skipped client-side` : ""}
                {mapping.email === null ? " · map the email column to continue" : ""}
                {roles.length === 0 ? " · pick at least one role" : ""}
              </span>
              <span className={styles.spacer} />
              <Button variant="contained" disabled={disabled || !upload.canScan} onClick={onScan}>
                Scan {rowCount.toLocaleString()} row{rowCount === 1 ? "" : "s"}
              </Button>
            </div>

            {problemCount > 0 ? (
              <div className={`${styles.banner} ${styles.bannerWarning}`}>
                <div>
                  {problemCount} row{problemCount === 1 ? "" : "s"} will not be sent — shown in the
                  preview&apos;s skipped group and in skipped.csv.
                </div>
                <ul className={styles.errorList}>
                  {built.problems.slice(0, PROBLEMS_SHOWN).map(problem => (
                    <li key={problem.lineNumber}>
                      Line {problem.lineNumber}: {problem.outcome.replace(/_/g, " ")} —{" "}
                      {problem.detail}
                    </li>
                  ))}
                  {problemCount > PROBLEMS_SHOWN ? (
                    <li>…and {problemCount - PROBLEMS_SHOWN} more</li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
